import React, { useState, useEffect, useCallback } from 'react';
import { AñoEscolar } from 'types/usuario.types';
import { TarifaAnual, TarifaAnualPayload, TipoDescuento, TipoDescuentoPayload } from 'types/pago.types';
import { tarifaService, tipoDescuentoService } from 'services/pagoService';
import { catalogoService, anioEscolarService } from 'services/usuarioService';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// ConfigPagosPage – Tarifas Anuales + Tipos de Descuento
// ============================================================

type Tab = 'tarifas' | 'descuentos' | 'años';

const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_PLUS   = 'M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z';
const D_EDIT   = 'M5.433 13.917l1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65ZM3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z';
const D_DEL    = 'M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z';
const D_X      = 'M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z';

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};
const NIVELES = ['INICIAL', 'PRIMARIA', 'SECUNDARIA'];
const MESES   = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                 'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const sol = (n: number) =>
  new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(n);

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

// ── Campo de formulario ───────────────────────────────────────
const Field: React.FC<{
  label: string; error?: string; children: React.ReactNode;
}> = ({ label, error, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{label}</label>
    {children}
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

const inputCls = (err?: string) =>
  `px-3 py-2 rounded-xl border text-sm text-slate-800 outline-none
   focus:ring-2 focus:ring-blue-500 transition-all bg-white
   ${err ? 'border-red-300' : 'border-slate-200'}`;

// ============================================================
// TAB TARIFAS ANUALES
// ============================================================

const TARIFA_EMPTY: TarifaAnualPayload = {
  anioEscolarId:  0,
  nivel:          'PRIMARIA',
  costoMatricula: 0,
  costoPension:   0,
  numPensiones:   10,
  mesInicio:      3,
};

type TarifaErrors = Partial<Record<keyof TarifaAnualPayload, string>>;

const TabTarifas: React.FC<{ añosEscolares: AñoEscolar[] }> = ({ añosEscolares }) => {
  const [tarifas, setTarifas]     = useState<TarifaAnual[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [anioFilter, setAnioFilter] = useState<number | ''>(
    añosEscolares.length > 0 ? añosEscolares[0].idAnioEscolar : ''
  );

  // Formulario inline
  const [showForm, setShowForm]   = useState(false);
  const [editId, setEditId]       = useState<number | null>(null);
  const [form, setForm]           = useState<TarifaAnualPayload>(TARIFA_EMPTY);
  const [formErrors, setFormErrors] = useState<TarifaErrors>({});
  const [rawNums, setRawNums]     = useState({ costoMatricula: '', costoPension: '', numPensiones: String(TARIFA_EMPTY.numPensiones) });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<number | null>(null);

  const load = useCallback(async (anioId: number | '' = anioFilter) => {
    setLoading(true); setError(null);
    try {
      const data = await tarifaService.listar(anioId || undefined);
      setTarifas(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar tarifas');
    } finally { setLoading(false); }
  }, [anioFilter]);

  useEffect(() => { load(); }, [load]);

  // Sync anioFilter cuando lleguen los años
  useEffect(() => {
    if (!anioFilter && añosEscolares.length > 0) {
      setAnioFilter(añosEscolares[0].idAnioEscolar);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [añosEscolares]);

  const openNew = () => {
    setEditId(null);
    setForm({
      ...TARIFA_EMPTY,
      anioEscolarId: typeof anioFilter === 'number' ? anioFilter : (añosEscolares[0]?.idAnioEscolar ?? 0),
    });
    setRawNums({ costoMatricula: '', costoPension: '', numPensiones: String(TARIFA_EMPTY.numPensiones) });
    setFormErrors({});
    setSubmitError(null);
    setShowForm(true);
  };

  const openEdit = (t: TarifaAnual) => {
    setEditId(t.idTarifa);
    setForm({
      anioEscolarId:  t.anioEscolarId,
      nivel:          t.nivel,
      costoMatricula: t.costoMatricula,
      costoPension:   t.costoPension,
      numPensiones:   t.numPensiones,
      mesInicio:      t.mesInicio,
    });
    setRawNums({
      costoMatricula: String(t.costoMatricula),
      costoPension:   String(t.costoPension),
      numPensiones:   String(t.numPensiones),
    });
    setFormErrors({});
    setSubmitError(null);
    setShowForm(true);
  };

  const validate = (): TarifaErrors => {
    const e: TarifaErrors = {};
    if (!form.anioEscolarId)      e.anioEscolarId  = 'Requerido';
    if (!form.nivel)              e.nivel          = 'Requerido';
    if (form.costoMatricula <= 0) e.costoMatricula = 'Debe ser mayor a 0';
    if (form.costoPension   <= 0) e.costoPension   = 'Debe ser mayor a 0';
    if (form.numPensiones < 1 || form.numPensiones > 12) e.numPensiones = 'Entre 1 y 12';
    if (form.mesInicio < 1 || form.mesInicio > 12) e.mesInicio = 'Mes inválido';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setFormErrors(e); return; }
    setSubmitting(true); setSubmitError(null);
    try {
      if (editId) {
        await tarifaService.modificar(editId, form);
      } else {
        await tarifaService.crear(form);
      }
      setShowForm(false);
      load();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id: number) => {
    try {
      await tarifaService.eliminar(id);
      setConfirmDel(null);
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al eliminar');
    }
  };

  const fc = (field: keyof TarifaAnualPayload) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const val = e.target.type === 'number' ? Number(e.target.value) : e.target.value;
    setForm(prev => ({ ...prev, [field]: val }));
    setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleNumChange = (field: 'costoMatricula' | 'costoPension' | 'numPensiones') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const raw = e.target.value;
    setRawNums(prev => ({ ...prev, [field]: raw }));
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) setForm(prev => ({ ...prev, [field]: parsed }));
    setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  return (
    <div className="space-y-4">

      {/* Barra superior */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <select
            value={anioFilter}
            onChange={e => {
              const v = e.target.value ? Number(e.target.value) : '';
              setAnioFilter(v);
              load(v);
            }}
            className="px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm
                       text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]">
            <option value="">Todos los años</option>
            {añosEscolares.map(a => (
              <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
            ))}
          </select>
          <span className="text-xs text-slate-400">{tarifas.length} tarifa{tarifas.length !== 1 ? 's' : ''}</span>
        </div>
        <button onClick={openNew}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white
                     font-semibold text-sm px-4 py-2 rounded-xl transition-colors shadow-sm">
          <Ico d={D_PLUS} />
          Nueva Tarifa
        </button>
      </div>

      {/* Formulario inline */}
      {showForm && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-700 text-sm">
              {editId ? 'Editar Tarifa' : 'Nueva Tarifa Anual'}
            </h3>
            <button onClick={() => setShowForm(false)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors">
              <Ico d={D_X} />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <Field label="Año escolar" error={formErrors.anioEscolarId}>
              <select value={form.anioEscolarId} onChange={fc('anioEscolarId')}
                className={inputCls(formErrors.anioEscolarId)}>
                <option value={0}>Selecciona...</option>
                {añosEscolares.map(a => (
                  <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
                ))}
              </select>
            </Field>

            <Field label="Nivel" error={formErrors.nivel}>
              <select value={form.nivel} onChange={fc('nivel')}
                className={inputCls(formErrors.nivel)}>
                {NIVELES.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>

            <Field label="Matrícula (S/)" error={formErrors.costoMatricula}>
              <input type="text" inputMode="decimal" placeholder="0.00"
                value={rawNums.costoMatricula}
                onChange={handleNumChange('costoMatricula')}
                className={inputCls(formErrors.costoMatricula)} />
            </Field>

            <Field label="Pensión mensual (S/)" error={formErrors.costoPension}>
              <input type="text" inputMode="decimal" placeholder="0.00"
                value={rawNums.costoPension}
                onChange={handleNumChange('costoPension')}
                className={inputCls(formErrors.costoPension)} />
            </Field>

            <Field label="N° pensiones" error={formErrors.numPensiones}>
              <input type="text" inputMode="numeric" placeholder="10"
                value={rawNums.numPensiones}
                onChange={handleNumChange('numPensiones')}
                className={inputCls(formErrors.numPensiones)} />
            </Field>

            <Field label="Mes de inicio" error={formErrors.mesInicio}>
              <select value={form.mesInicio} onChange={fc('mesInicio')}
                className={inputCls(formErrors.mesInicio)}>
                {MESES.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </Field>
          </div>

          {submitError && (
            <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{submitError}</p>
          )}

          <div className="flex gap-3 justify-end">
            <button onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold
                         text-slate-600 hover:bg-slate-100 transition-colors">
              Cancelar
            </button>
            <button onClick={handleSubmit} disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                         text-white text-sm font-semibold transition-colors disabled:opacity-60">
              {submitting ? <SpinnerIcon /> : null}
              {editId ? 'Guardar cambios' : 'Crear tarifa'}
            </button>
          </div>
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-16"><SpinnerIcon /></div>
        ) : error ? (
          <div className="text-center py-10 text-red-500 text-sm">{error}</div>
        ) : tarifas.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-400 text-sm font-medium">No hay tarifas configuradas</p>
            <p className="text-slate-300 text-xs mt-1">Crea la primera tarifa con el botón superior</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                {['Año', 'Nivel'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-400
                                         uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
                {['Matrícula', 'Pensión mensual'].map(h => (
                  <th key={h} className="text-left px-4 py-3 whitespace-nowrap">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{h}</p>
                    <p className="text-[10px] font-normal text-slate-300 normal-case tracking-normal mt-0.5">
                      Monto referencial, puede variar
                    </p>
                  </th>
                ))}
                {['Meses de pago', 'Estado', 'Acciones'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-400
                                         uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {tarifas.map(t => (
                <tr key={t.idTarifa} className="hover:bg-slate-50 transition-colors">

                  {/* Año */}
                  <td className="px-4 py-3.5 text-sm font-semibold text-slate-700">{t.anio}</td>

                  {/* Nivel */}
                  <td className="px-4 py-3.5">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full
                      ${NIVEL_COLOR[t.nivel] ?? 'bg-slate-100 text-slate-600'}`}>
                      {t.nivel}
                    </span>
                  </td>

                  {/* Costo matrícula */}
                  <td className="px-4 py-3.5 text-sm font-semibold text-slate-800 tabular-nums whitespace-nowrap">
                    {sol(t.costoMatricula)}
                  </td>

                  {/* Costo pensión */}
                  <td className="px-4 py-3.5">
                    <p className="text-sm font-semibold text-slate-800 tabular-nums">{sol(t.costoPension)}</p>
                    <p className="text-xs text-slate-400">{t.numPensiones} cuotas</p>
                  </td>

                  {/* Meses de pago */}
                  <td className="px-4 py-3.5">
                    <div className="flex flex-wrap gap-1 max-w-[220px]">
                      {t.mesesPension.map(m => (
                        <span key={m.numero}
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md
                                     bg-blue-50 text-blue-600 capitalize whitespace-nowrap">
                          {m.mesNombre.slice(0, 3)}
                        </span>
                      ))}
                    </div>
                  </td>

                  {/* Estado activo */}
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold
                                      px-2.5 py-1 rounded-full
                      ${t.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full
                        ${t.activo ? 'bg-green-500' : 'bg-slate-400'}`} />
                      {t.activo ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>

                  {/* Acciones */}
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(t)} title="Editar"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                        <Ico d={D_EDIT} />
                      </button>
                      {confirmDel === t.idTarifa ? (
                        <div className="flex items-center gap-1.5 ml-1">
                          <span className="text-xs text-slate-500">¿Eliminar?</span>
                          <button onClick={() => handleDelete(t.idTarifa)}
                            className="text-xs font-bold text-red-600 hover:underline">Sí</button>
                          <button onClick={() => setConfirmDel(null)}
                            className="text-xs font-bold text-slate-400 hover:underline">No</button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmDel(t.idTarifa)} title="Eliminar"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                          <Ico d={D_DEL} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ============================================================
// TAB TIPOS DE DESCUENTO
// ============================================================

const DESC_EMPTY: TipoDescuentoPayload = {
  nombre:          '',
  tipo:            'PORCENTAJE',
  valor:           0,
  aplicaMatricula: false,
  aplicaPension:   true,
  activo:          true,
};

type DescErrors = Partial<Record<keyof TipoDescuentoPayload, string>>;

const TabDescuentos: React.FC = () => {
  const [tipos, setTipos]         = useState<TipoDescuento[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const [showForm, setShowForm]   = useState(false);
  const [editId, setEditId]       = useState<number | null>(null);
  const [form, setForm]           = useState<TipoDescuentoPayload>(DESC_EMPTY);
  const [formErrors, setFormErrors] = useState<DescErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await tipoDescuentoService.listar();
      setTipos(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar tipos de descuento');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditId(null);
    setForm(DESC_EMPTY);
    setFormErrors({});
    setSubmitError(null);
    setShowForm(true);
  };

  const openEdit = (t: TipoDescuento) => {
    setEditId(t.idTipoDescuento);
    setForm({
      nombre:          t.nombre,
      tipo:            t.tipo,
      valor:           t.valor,
      aplicaMatricula: t.aplicaMatricula,
      aplicaPension:   t.aplicaPension,
      activo:          t.activo,
    });
    setFormErrors({});
    setSubmitError(null);
    setShowForm(true);
  };

  const validate = (): DescErrors => {
    const e: DescErrors = {};
    if (!form.nombre.trim()) e.nombre = 'Requerido';
    if (form.valor <= 0)     e.valor  = 'Debe ser mayor a 0';
    if (form.tipo === 'PORCENTAJE' && form.valor > 100)
      e.valor = 'El porcentaje no puede superar 100';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setFormErrors(e); return; }
    setSubmitting(true); setSubmitError(null);
    try {
      if (editId) {
        await tipoDescuentoService.modificar(editId, form);
      } else {
        await tipoDescuentoService.crear(form);
      }
      setShowForm(false);
      load();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id: number) => {
    try {
      await tipoDescuentoService.eliminar(id);
      setConfirmDel(null);
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al eliminar');
    }
  };

  const fc = (field: keyof TipoDescuentoPayload) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { type, value } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    const val = type === 'checkbox' ? checked : type === 'number' ? Number(value) : value;
    setForm(prev => ({ ...prev, [field]: val }));
    setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  return (
    <div className="space-y-4">

      {/* Barra superior */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">{tipos.length} tipo{tipos.length !== 1 ? 's' : ''} de descuento</span>
        <button onClick={openNew}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white
                     font-semibold text-sm px-4 py-2 rounded-xl transition-colors shadow-sm">
          <Ico d={D_PLUS} />
          Nuevo Descuento
        </button>
      </div>

      {/* Formulario inline */}
      {showForm && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-700 text-sm">
              {editId ? 'Editar Tipo de Descuento' : 'Nuevo Tipo de Descuento'}
            </h3>
            <button onClick={() => setShowForm(false)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors">
              <Ico d={D_X} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Nombre" error={formErrors.nombre}>
              <input type="text" value={form.nombre} onChange={fc('nombre')}
                placeholder="Ej: Descuento puntualidad"
                className={inputCls(formErrors.nombre)} />
            </Field>

            <Field label="Tipo" error={formErrors.tipo}>
              <select value={form.tipo} onChange={fc('tipo')}
                className={inputCls(formErrors.tipo)}>
                <option value="PORCENTAJE">Porcentaje (%)</option>
                <option value="MONTO_FIJO">Monto fijo (S/)</option>
              </select>
            </Field>

            <Field
              label={form.tipo === 'PORCENTAJE' ? 'Valor (%)' : 'Valor (S/)'}
              error={formErrors.valor}>
              <input type="number" min={0} step={form.tipo === 'PORCENTAJE' ? 1 : 0.01}
                max={form.tipo === 'PORCENTAJE' ? 100 : undefined}
                value={form.valor === 0 ? '' : form.valor} onChange={fc('valor')}
                placeholder="0"
                className={inputCls(formErrors.valor)} />
            </Field>

            <Field label="Estado">
              <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200
                                bg-white cursor-pointer h-[38px]">
                <input type="checkbox" checked={form.activo} onChange={fc('activo')}
                  className="w-4 h-4 rounded accent-blue-700" />
                <span className="text-sm text-slate-700">Activo</span>
              </label>
            </Field>
          </div>

          {/* Aplica a */}
          <div className="flex flex-wrap gap-4">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide self-center">
              Aplica a:
            </p>
            <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200
                              bg-white cursor-pointer">
              <input type="checkbox" checked={form.aplicaMatricula} onChange={fc('aplicaMatricula')}
                className="w-4 h-4 rounded accent-blue-700" />
              <span className="text-sm text-slate-700">Matrícula</span>
            </label>
            <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200
                              bg-white cursor-pointer">
              <input type="checkbox" checked={form.aplicaPension} onChange={fc('aplicaPension')}
                className="w-4 h-4 rounded accent-blue-700" />
              <span className="text-sm text-slate-700">Pensión</span>
            </label>
          </div>

          {submitError && (
            <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{submitError}</p>
          )}

          <div className="flex gap-3 justify-end">
            <button onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold
                         text-slate-600 hover:bg-slate-100 transition-colors">
              Cancelar
            </button>
            <button onClick={handleSubmit} disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                         text-white text-sm font-semibold transition-colors disabled:opacity-60">
              {submitting ? <SpinnerIcon /> : null}
              {editId ? 'Guardar cambios' : 'Crear descuento'}
            </button>
          </div>
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-16"><SpinnerIcon /></div>
        ) : error ? (
          <div className="text-center py-10 text-red-500 text-sm">{error}</div>
        ) : tipos.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-400 text-sm font-medium">No hay tipos de descuento configurados</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                {['Nombre', 'Tipo', 'Valor', 'Aplica a', 'Estado', 'Acciones'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-400
                                         uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {tipos.map(t => (
                <tr key={t.idTipoDescuento} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3.5 font-semibold text-sm text-slate-800">{t.nombre}</td>
                  <td className="px-4 py-3.5">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full
                      ${t.tipo === 'PORCENTAJE'
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-emerald-100 text-emerald-700'}`}>
                      {t.tipo === 'PORCENTAJE' ? 'Porcentaje' : 'Monto fijo'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm font-semibold text-slate-800 tabular-nums whitespace-nowrap">
                    {t.tipo === 'PORCENTAJE'
                      ? `${t.valor}%`
                      : sol(t.valor)}
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {t.aplicaMatricula && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-md
                                         bg-amber-100 text-amber-700">
                          Matrícula
                        </span>
                      )}
                      {t.aplicaPension && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-md
                                         bg-blue-100 text-blue-700">
                          Pensión
                        </span>
                      )}
                      {!t.aplicaMatricula && !t.aplicaPension && (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full
                      ${t.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${t.activo ? 'bg-green-500' : 'bg-slate-400'}`}/>
                      {t.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(t)} title="Editar"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                        <Ico d={D_EDIT} />
                      </button>
                      {confirmDel === t.idTipoDescuento ? (
                        <div className="flex items-center gap-1.5 ml-1">
                          <span className="text-xs text-slate-500">¿Eliminar?</span>
                          <button onClick={() => handleDelete(t.idTipoDescuento)}
                            className="text-xs font-bold text-red-600 hover:underline">Sí</button>
                          <button onClick={() => setConfirmDel(null)}
                            className="text-xs font-bold text-slate-400 hover:underline">No</button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmDel(t.idTipoDescuento)} title="Eliminar"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                          <Ico d={D_DEL} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ============================================================
// TAB AÑOS ESCOLARES
// ============================================================

const D_TOGGLE_ON  = 'M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z';
const D_TOGGLE_OFF = 'M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z';

const FASE_LABEL: Record<string, string> = {
  REGULAR:      'Regular',
  RECUPERACION: 'Recuperación',
};

const TabAños: React.FC = () => {
  const [items, setItems]         = useState<AñoEscolar[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [showForm, setShowForm]   = useState(false);
  const [anioInput, setAnioInput] = useState('');
  const [faseInput, setFaseInput] = useState<'REGULAR' | 'RECUPERACION'>('REGULAR');
  const [anioError, setAnioError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editId, setEditId]       = useState<number | null>(null);
  const [editAnio, setEditAnio]   = useState('');
  const [editFase, setEditFase]   = useState<'REGULAR' | 'RECUPERACION'>('REGULAR');
  const [editError, setEditError] = useState('');
  const [toggling, setToggling]   = useState<number | null>(null);
  const [confirmItem, setConfirmItem] = useState<AñoEscolar | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setItems(await anioEscolarService.listarTodos());
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar años escolares');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleCrear = async () => {
    const n = Number(anioInput);
    if (!anioInput || isNaN(n) || n < 2000 || n > 2100) {
      setAnioError('Ingresa un año válido (2000–2100)');
      return;
    }
    setSubmitting(true);
    setAnioError('');
    try {
      const nuevo = await anioEscolarService.crear({ anio: n, fase: faseInput });
      setItems(prev => [nuevo, ...prev].sort((a, b) => b.anio - a.anio));
      setAnioInput('');
      setFaseInput('REGULAR');
      setShowForm(false);
    } catch (e: any) {
      setAnioError(e.message ?? 'Error al crear');
    } finally { setSubmitting(false); }
  };

  const handleGuardarEdicion = async (id: number) => {
    const n = Number(editAnio);
    if (!editAnio || isNaN(n) || n < 2000 || n > 2100) {
      setEditError('Año inválido');
      return;
    }
    setSubmitting(true);
    setEditError('');
    try {
      const updated = await anioEscolarService.actualizar(id, { anio: n, fase: editFase });
      setItems(prev => prev.map(a => a.idAnioEscolar === id ? updated : a));
      setEditId(null);
    } catch (e: any) {
      setEditError(e.message ?? 'Error al guardar');
    } finally { setSubmitting(false); }
  };

  const handleToggleActivo = async (item: AñoEscolar) => {
    setConfirmItem(null);
    setToggleError(null);
    setToggling(item.idAnioEscolar);
    try {
      const updated = await anioEscolarService.actualizar(item.idAnioEscolar, { anio: item.anio, activo: !item.activo });
      setItems(prev => prev.map(a => a.idAnioEscolar === item.idAnioEscolar ? updated : a));
    } catch (e: unknown) {
      setToggleError(e instanceof Error ? e.message : 'Error al cambiar el estado del año');
    } finally { setToggling(null); }
  };

  return (
    <div className="space-y-5">
      {/* Cabecera */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Registra y gestiona los años escolares del sistema.
          Solo los años <span className="font-semibold text-green-700">activos</span> están disponibles para matrículas y cobros.
        </p>
        {!showForm && (
          <button onClick={() => { setShowForm(true); setAnioInput(''); setAnioError(''); }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                       text-white text-sm font-semibold transition-colors shadow-sm flex-shrink-0 ml-4">
            <Ico d={D_PLUS} />
            Nuevo año
          </button>
        )}
      </div>

      {/* Formulario de creación */}
      {showForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
          <p className="text-sm font-bold text-blue-800 mb-3">Registrar nuevo año escolar</p>
          <div className="flex flex-wrap items-start gap-3">
            {/* Año */}
            <div className="flex flex-col gap-1 w-36">
              <label className="text-xs font-bold text-blue-700 uppercase tracking-wide">Año *</label>
              <input
                type="number"
                placeholder="Ej: 2026"
                value={anioInput}
                onChange={e => setAnioInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCrear()}
                className={inputCls(anioError)}
                min={2000} max={2100}
              />
              {anioError && <p className="text-xs text-red-500">{anioError}</p>}
            </div>
            {/* Fase */}
            <div className="flex flex-col gap-1 w-44">
              <label className="text-xs font-bold text-blue-700 uppercase tracking-wide">Fase *</label>
              <select
                value={faseInput}
                onChange={e => setFaseInput(e.target.value as 'REGULAR' | 'RECUPERACION')}
                className={inputCls('')}
              >
                <option value="REGULAR">Regular</option>
                <option value="RECUPERACION">Recuperación</option>
              </select>
              {faseInput === 'RECUPERACION' && (
                <p className="text-xs text-amber-700">
                  Solo se podrán matricular estudiantes con estado <strong>EN_RECUPERACION</strong>.
                  Puede coexistir con un año Regular del mismo período.
                </p>
              )}
              {faseInput === 'REGULAR' && (
                <p className="text-xs text-slate-500">
                  Año académico estándar con pensiones mensuales y todos los procesos regulares.
                </p>
              )}
            </div>
            {/* Botones */}
            <div className="flex items-end gap-2 pb-0.5 mt-auto">
              <button onClick={handleCrear} disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                           text-white text-sm font-semibold transition-colors disabled:opacity-60">
                {submitting ? <SpinnerIcon /> : null}
                Guardar
              </button>
              <button onClick={() => { setShowForm(false); setFaseInput('REGULAR'); }}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm
                           font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error de apertura/cierre */}
      {toggleError && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl px-5 py-4">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5">
            <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z" clipRule="evenodd"/>
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-800">No se pudo cambiar el estado</p>
            <p className="text-sm text-red-700 mt-0.5">{toggleError}</p>
            {toggleError.toLowerCase().includes('activo') && (
              <p className="text-xs text-red-600 mt-1.5 bg-red-100 rounded-lg px-3 py-2">
                Cierra primero el año vigente haciendo clic en <strong>"Cerrar año"</strong> y luego apertura este.
              </p>
            )}
          </div>
          <button onClick={() => setToggleError(null)}
            className="text-red-400 hover:text-red-600 p-1 rounded-lg hover:bg-red-100 transition-colors flex-shrink-0">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z"/>
            </svg>
          </button>
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-16"><SpinnerIcon /></div>
        ) : error ? (
          <div className="text-center py-10 text-red-500 text-sm">{error}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-400 text-sm font-medium">No hay años escolares registrados</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                {['Año', 'Fase', 'Estado', 'Acciones'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-400
                                         uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {items.map(a => (
                <tr key={a.idAnioEscolar} className={`transition-colors ${!a.activo ? 'opacity-50' : 'hover:bg-slate-50'}`}>
                  {/* Año */}
                  <td className="px-4 py-3.5">
                    {editId === a.idAnioEscolar ? (
                      <div className="flex flex-col gap-1">
                        <input
                          type="number"
                          value={editAnio}
                          onChange={e => setEditAnio(e.target.value)}
                          className={`${inputCls(editError)} w-28`}
                          min={2000} max={2100}
                          autoFocus
                        />
                        {editError && <p className="text-xs text-red-500">{editError}</p>}
                      </div>
                    ) : (
                      <span className="text-lg font-bold text-slate-800 tabular-nums">{a.anio}</span>
                    )}
                  </td>

                  {/* Fase */}
                  <td className="px-4 py-3.5">
                    {editId === a.idAnioEscolar ? (
                      <select
                        value={editFase}
                        onChange={e => setEditFase(e.target.value as 'REGULAR' | 'RECUPERACION')}
                        className={`${inputCls('')} w-40`}
                      >
                        <option value="REGULAR">Regular</option>
                        <option value="RECUPERACION">Recuperación</option>
                      </select>
                    ) : (
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full
                        ${a.fase === 'RECUPERACION'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-blue-100 text-blue-700'}`}>
                        {FASE_LABEL[a.fase] ?? a.fase}
                      </span>
                    )}
                  </td>

                  {/* Estado */}
                  <td className="px-4 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full
                      ${a.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${a.activo ? 'bg-green-500' : 'bg-slate-400'}`}/>
                      {a.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>

                  {/* Acciones */}
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1">
                      {editId === a.idAnioEscolar ? (
                        <>
                          <button onClick={() => handleGuardarEdicion(a.idAnioEscolar)} disabled={submitting}
                            className="px-3 py-1.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white
                                       text-xs font-bold transition-colors disabled:opacity-60">
                            Guardar
                          </button>
                          <button onClick={() => setEditId(null)}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs
                                       font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => { setEditId(a.idAnioEscolar); setEditAnio(String(a.anio)); setEditFase(a.fase); setEditError(''); }}
                            title="Editar año"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                            <Ico d={D_EDIT} />
                          </button>
                          <button
                            onClick={() => setConfirmItem(a)}
                            disabled={toggling === a.idAnioEscolar}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                                        transition-colors disabled:opacity-60
                              ${a.activo
                                ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-100'
                                : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-100'}`}>
                            {toggling === a.idAnioEscolar ? <SpinnerIcon /> : null}
                            {a.activo ? 'Cerrar año' : 'Aperturar'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de confirmación cerrar/aperturar */}
      {confirmItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: 'rgba(15,23,42,0.45)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            {/* Icono + título */}
            <div className="flex flex-col items-center text-center gap-2">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center
                ${confirmItem.activo ? 'bg-red-100' : 'bg-green-100'}`}>
                {confirmItem.activo ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
                       className="w-7 h-7 text-red-600">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
                       className="w-7 h-7 text-green-600">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M13.5 10.5V6.75a4.5 4.5 0 0 0-4.478 3.975M3.75 10.5h16.5m-16.5 0a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25h16.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25" />
                  </svg>
                )}
              </div>
              <h3 className="text-base font-bold text-slate-800">
                {confirmItem.activo ? 'Cerrar año escolar' : 'Aperturar año escolar'}
              </h3>
              <p className="text-sm text-slate-500 leading-snug">
                {confirmItem.activo
                  ? <>¿Confirmas el <span className="font-semibold text-red-600">cierre</span> del año <span className="font-bold text-slate-700">{confirmItem.anio}</span>? Ya no estará disponible para matrículas ni cobros.</>
                  : <>¿Confirmas la <span className="font-semibold text-green-700">apertura</span> del año <span className="font-bold text-slate-700">{confirmItem.anio}</span>? Quedará disponible para matrículas y cobros.</>
                }
              </p>
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setConfirmItem(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm
                           font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                Cancelar
              </button>
              <button
                onClick={() => handleToggleActivo(confirmItem)}
                disabled={toggling === confirmItem.idAnioEscolar}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
                            text-sm font-bold text-white transition-colors disabled:opacity-60
                  ${confirmItem.activo
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'}`}>
                {toggling === confirmItem.idAnioEscolar && <SpinnerIcon />}
                {confirmItem.activo ? 'Sí, cerrar' : 'Sí, aperturar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

const ConfigPagosPage: React.FC = () => {
  const [tab, setTab]                   = useState<Tab>('años');
  const [añosEscolares, setAñosEscolares] = useState<AñoEscolar[]>([]);

  useEffect(() => {
    catalogoService.getAñosEscolares()
      .then(data => setAñosEscolares(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const tabs: { key: Tab; label: string; desc: string }[] = [
    { key: 'años',       label: 'Años Escolares',      desc: 'Registrar y activar años escolares' },
    { key: 'tarifas',    label: 'Tarifas Anuales',     desc: 'Costo de matrícula y pensión por nivel' },
    { key: 'descuentos', label: 'Tipos de Descuento',  desc: 'Becas, hermanos y otros descuentos' },
  ];

  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div>
        <h1 className="text-xl font-bold text-slate-800">Configuración de Pagos</h1>
        <p className="text-slate-400 text-sm mt-0.5">
          Tarifas anuales y tipos de descuento disponibles
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-100">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 sm:flex-none px-6 py-4 text-sm font-semibold transition-colors
                text-left border-b-2 -mb-px
                ${tab === t.key
                  ? 'border-blue-600 text-blue-700 bg-blue-50/40'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
              <span className="block">{t.label}</span>
              <span className="block text-xs font-normal text-slate-400 mt-0.5">{t.desc}</span>
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === 'años'       && <TabAños />}
          {tab === 'tarifas'    && <TabTarifas añosEscolares={añosEscolares} />}
          {tab === 'descuentos' && <TabDescuentos />}
        </div>
      </div>
    </div>
  );
};

export default ConfigPagosPage;
